import { useId } from 'react';
import { Info, Mail } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import NoteField from '../share/recipients/NoteField';
import RecipientPicker from '../share/recipients/RecipientPicker';
import {
  CLIENT_TEAM_PICKER_COPY,
  NOTIFY_BLOCK_COPY,
  NOTIFY_COPY
} from './notifyCopy';
import type { NotifyTeam } from './useNotifyTeam';

interface NotifyTeamBlockProps {
  notify: NotifyTeam;
  /** Locks the block while the reveal or its email is in flight. */
  disabled: boolean;
  /** The id of the line saying why the email cannot go, which the confirm
   *  button is described by. */
  lineId: string;
}

/**
 * DV-3563: the Make Visible dialog's Send Email Notification block (desktop
 * prototype, 2026-10-09): a checkbox header, and while it is checked the
 * shared recipient picker and note. Unchecked, the rest collapses.
 */
const NotifyTeamBlock = ({ notify, disabled, lineId }: NotifyTeamBlockProps) => {
  const id = useId();
  const { block } = notify;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-background">
        <label
          htmlFor={`${id}-checkbox`}
          // Below lg the checkbox's root is itself a 44px target, so the
          // row's own padding steps back (as the picker's member rows).
          className="flex cursor-pointer items-start gap-3 bg-muted px-3 py-3 has-[:disabled]:cursor-not-allowed max-lg:items-center max-lg:py-1"
        >
          <Checkbox
            id={`${id}-checkbox`}
            checked={notify.emailOn}
            onCheckedChange={(checked) => notify.setEmailOn(checked === true)}
            disabled={disabled || !notify.offered}
            aria-labelledby={`${id}-heading`}
            aria-describedby={`${id}-description`}
            className="mt-0.5 max-lg:mt-0"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span id={`${id}-heading`} className="text-sm font-semibold">
              {NOTIFY_COPY.heading}
            </span>
            <span
              id={`${id}-description`}
              className="text-xs text-muted-foreground"
            >
              {NOTIFY_COPY.description}
            </span>
          </span>
          <Mail
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground max-lg:mt-0"
            aria-hidden
          />
        </label>
        {notify.emailOn && (
          <>
            <div className="border-t border-border">
              <RecipientPicker
                team={notify.team}
                disabled={disabled}
                renderNoMatch={NOTIFY_COPY.noMatch}
                empty={NOTIFY_BLOCK_COPY['no-team']}
                copy={CLIENT_TEAM_PICKER_COPY}
              />
            </div>
            <NoteField
              label={NOTIFY_COPY.noteLabel}
              value={notify.note}
              onChange={notify.setNote}
              disabled={disabled}
            />
          </>
        )}
      </div>
      {block !== null && (
        <p
          id={lineId}
          className="m-0 flex items-start gap-2 text-sm text-muted-foreground"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{NOTIFY_BLOCK_COPY[block]}</span>
        </p>
      )}
    </div>
  );
};

export default NotifyTeamBlock;
